import React from 'react';
import { Link } from 'react-router-dom';

export interface PageSection {
  id: number;
  page_id: number;
  section_type: 'hero' | 'text' | 'image_left' | 'image_right' | 'image_full' | 'cards' | 'two_col' | 'gallery_grid';
  title?: string;
  subtitle?: string;
  body?: string;
  image_url?: string;
  button_label?: string;
  button_link?: string;
  badge_text?: string;
  highlight_word?: string;
  bg_color?: string;
  sort_order: number;
}

export interface PageData {
  id: number;
  menu_id: number;
  slug: string;
  menu_title: string;
  menu_link: string;
  sections: PageSection[];
}

const bgStyle = (color?: string): React.CSSProperties => {
  if (!color) return {};
  // hex colour
  if (color.startsWith('#')) return { backgroundColor: color };
  // legacy named values kept for backwards compat
  switch (color) {
    case 'dark':   return { backgroundColor: '#0A1D2D', color: '#fff' };
    case 'orange': return { backgroundColor: '#EF5D22', color: '#fff' };
    case 'gray':   return { backgroundColor: '#f9fafb' };
    default:       return { backgroundColor: '#ffffff' };
  }
};

const isDark = (color?: string) => {
  if (!color) return false;
  if (color === 'dark') return true;
  if (color === 'orange') return true;
  if (!color.startsWith('#')) return false;
  // Rough luminance check for hex
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0,2), 16);
  const g = parseInt(hex.slice(2,4), 16);
  const b = parseInt(hex.slice(4,6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
};

/** Splits title at highlight_word to render it in brand orange */
const SplitTitle = ({ title, highlight }: { title?: string; highlight?: string }) => {
  if (!title) return null;
  if (!highlight || !title.includes(highlight)) {
    return <>{title}</>;
  }
  const [before, after] = title.split(highlight);
  return <>{before}<span className="text-[#EF5D22]">{highlight}</span>{after}</>;
};

// ── Layout renderers ──────────────────────────────────────────────────────────

const HeroSection = ({ s }: { s: PageSection }) => (
  <section className="relative min-h-[500px] flex items-center overflow-hidden">
    {s.image_url && (
      <img src={s.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
    )}
    <div className="absolute inset-0 bg-black/50" />
    <div className="relative z-10 container mx-auto px-6 sm:px-10 py-24 text-white text-center">
      {s.badge_text && (
        <span className="inline-block px-4 py-1 bg-[#EF5D22] rounded-full text-xs font-bold uppercase tracking-widest mb-6">
          {s.badge_text}
        </span>
      )}
      {s.title && (
        <h1 className="text-4xl sm:text-6xl font-extrabold leading-tight mb-6 drop-shadow-lg">
          <SplitTitle title={s.title} highlight={s.highlight_word} />
        </h1>
      )}
      {s.subtitle && <p className={`text-xl text-white/90 max-w-2xl mx-auto mb-8`}>{s.subtitle}</p>}
      {s.body && <div className="text-lg text-white/80 max-w-2xl mx-auto mb-10 prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: s.body }} />}
      {s.button_label && s.button_link && (
        <Link to={s.button_link} className="inline-block bg-[#EF5D22] hover:bg-[#d9541a] text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all hover:scale-105">
          {s.button_label}
        </Link>
      )}
    </div>
  </section>
);

const TextSection = ({ s }: { s: PageSection }) => (
  <section className="py-20 px-6 sm:px-10" style={bgStyle(s.bg_color)}>
    <div className={`container mx-auto max-w-3xl text-center ${isDark(s.bg_color) ? 'text-white' : 'text-gray-900'}`}>
      {s.badge_text && (
        <span className="inline-block text-[#EF5D22] font-bold tracking-widest uppercase text-sm mb-4">
          {s.badge_text}
        </span>
      )}
      {s.title && (
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
          <SplitTitle title={s.title} highlight={s.highlight_word} />
        </h2>
      )}
      {s.subtitle && <p className={`text-lg mb-4 ${isDark(s.bg_color) ? 'text-white/70' : 'text-gray-500'}`}>{s.subtitle}</p>}
      {s.body && (
        <div
          className={`text-lg leading-relaxed prose prose-sm max-w-none ${isDark(s.bg_color) ? 'prose-invert text-white/80' : 'text-gray-600'}`}
          dangerouslySetInnerHTML={{ __html: s.body }}
        />
      )}
      {s.button_label && s.button_link && (
        <Link to={s.button_link} className="inline-block mt-6 bg-[#EF5D22] hover:bg-[#d9541a] text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all hover:scale-105">
          {s.button_label}
        </Link>
      )}
    </div>
  </section>
);

const ImageSideSection = ({ s, imgLeft }: { s: PageSection; imgLeft: boolean }) => (
  <section className="py-20 px-6 sm:px-10" style={bgStyle(s.bg_color)}>
    <div className={`container mx-auto max-w-6xl grid md:grid-cols-2 gap-12 items-center ${imgLeft ? '' : 'md:[direction:rtl]'}`}>
      {s.image_url && (
        <div className="md:[direction:ltr]">
          <img src={s.image_url} alt={s.title || ''} className="w-full rounded-2xl shadow-xl object-cover max-h-[450px]" />
        </div>
      )}
      <div className={`md:[direction:ltr] space-y-4 ${isDark(s.bg_color) ? 'text-white' : 'text-gray-900'}`}>
        {s.badge_text && (
          <span className="inline-block text-[#EF5D22] font-bold tracking-widest uppercase text-sm">
            {s.badge_text}
          </span>
        )}
        {s.title && (
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            <SplitTitle title={s.title} highlight={s.highlight_word} />
          </h2>
        )}
        {s.subtitle && <p className={isDark(s.bg_color) ? 'text-white/70 text-lg' : 'text-gray-500 text-lg'}>{s.subtitle}</p>}
        {s.body && (
          <div
            className={`leading-relaxed prose prose-sm max-w-none ${isDark(s.bg_color) ? 'prose-invert text-white/80' : 'text-gray-600'}`}
            dangerouslySetInnerHTML={{ __html: s.body }}
          />
        )}
        {s.button_label && s.button_link && (
          <Link to={s.button_link} className="inline-block mt-2 bg-[#EF5D22] hover:bg-[#d9541a] text-white font-bold px-6 py-2.5 rounded-xl shadow-lg transition-all hover:scale-105">
            {s.button_label}
          </Link>
        )}
      </div>
    </div>
  </section>
);

const ImageFullSection = ({ s }: { s: PageSection }) => (
  <section className="relative py-32 px-6 sm:px-10 overflow-hidden">
    {s.image_url && (
      <img src={s.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
    )}
    <div className="absolute inset-0 bg-black/55" />
    <div className="relative z-10 container mx-auto max-w-3xl text-center text-white">
      {s.badge_text && <span className="inline-block text-[#EF5D22] font-bold tracking-widest uppercase text-sm mb-4">{s.badge_text}</span>}
      {s.title && <h2 className="text-4xl sm:text-5xl font-extrabold mb-4"><SplitTitle title={s.title} highlight={s.highlight_word} /></h2>}
      {s.body && (
        <div
          className="text-white/85 text-lg leading-relaxed prose prose-invert prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: s.body }}
        />
      )}
      {s.button_label && s.button_link && (
        <Link to={s.button_link} className="inline-block mt-6 bg-[#EF5D22] hover:bg-[#d9541a] text-white font-bold px-8 py-3 rounded-xl shadow-lg transition-all hover:scale-105">
          {s.button_label}
        </Link>
      )}
    </div>
  </section>
);

const CardsSection = ({ s }: { s: PageSection }) => {
  // body is stored as JSON array: [{title,text,icon?}] or plain text lines
  let cards: { title: string; text: string }[] = [];
  try { cards = JSON.parse(s.body || '[]'); } catch {
    cards = (s.body || '').split('\n').filter(Boolean).map(line => ({ title: '', text: line }));
  }
  return (
    <section className="py-20 px-6 sm:px-10" style={bgStyle(s.bg_color)}>
      <div className="container mx-auto max-w-6xl">
        {(s.badge_text || s.title) && (
          <div className={`text-center mb-12 ${isDark(s.bg_color) ? 'text-white' : 'text-gray-900'}`}>
            {s.badge_text && <span className="inline-block text-[#EF5D22] font-bold tracking-widest uppercase text-sm mb-3">{s.badge_text}</span>}
            {s.title && <h2 className="text-3xl sm:text-4xl font-extrabold"><SplitTitle title={s.title} highlight={s.highlight_word} /></h2>}
            {s.subtitle && <p className={`mt-2 ${isDark(s.bg_color) ? 'text-white/70' : 'text-gray-500'}`}>{s.subtitle}</p>}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              {card.title && <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>}
              <p className="text-gray-600 text-sm leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TwoColSection = ({ s }: { s: PageSection }) => {
  // body split by "|||" separator for two columns
  const [col1 = '', col2 = ''] = (s.body || '').split('|||');
  return (
    <section className="py-20 px-6 sm:px-10" style={bgStyle(s.bg_color)}>
      <div className={`container mx-auto max-w-6xl ${isDark(s.bg_color) ? 'text-white' : 'text-gray-900'}`}>
        {s.title && (
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-10">
            <SplitTitle title={s.title} highlight={s.highlight_word} />
          </h2>
        )}
        <div className="grid md:grid-cols-2 gap-10">
          <div
            className={`leading-relaxed prose prose-sm max-w-none ${isDark(s.bg_color) ? 'prose-invert text-white/80' : 'text-gray-600'}`}
            dangerouslySetInnerHTML={{ __html: col1.trim() }}
          />
          <div
            className={`leading-relaxed prose prose-sm max-w-none ${isDark(s.bg_color) ? 'prose-invert text-white/80' : 'text-gray-600'}`}
            dangerouslySetInnerHTML={{ __html: col2.trim() }}
          />
        </div>
      </div>
    </section>
  );
};

const GalleryGridSection = ({ s }: { s: PageSection }) => {
  // image_url is a comma-separated list of image URLs
  const images = (s.image_url || '').split(',').map(u => u.trim()).filter(Boolean);
  return (
    <section className="py-20 px-6 sm:px-10" style={bgStyle(s.bg_color)}>
      <div className={`container mx-auto max-w-6xl ${isDark(s.bg_color) ? 'text-white' : 'text-gray-900'}`}>
        {s.title && <h2 className="text-3xl font-extrabold text-center mb-10"><SplitTitle title={s.title} highlight={s.highlight_word} /></h2>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((url, i) => (
            <div key={i} className="aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
              <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Main renderer ─────────────────────────────────────────────────────────────

const SectionRenderer = ({ section }: { section: PageSection }) => {
  switch (section.section_type) {
    case 'hero':         return <HeroSection s={section} />;
    case 'image_left':   return <ImageSideSection s={section} imgLeft={true} />;
    case 'image_right':  return <ImageSideSection s={section} imgLeft={false} />;
    case 'image_full':   return <ImageFullSection s={section} />;
    case 'cards':        return <CardsSection s={section} />;
    case 'two_col':      return <TwoColSection s={section} />;
    case 'gallery_grid': return <GalleryGridSection s={section} />;
    default:             return <TextSection s={section} />;
  }
};

interface PageRendererProps {
  page: PageData;
}

const PageRenderer: React.FC<PageRendererProps> = ({ page }) => {
  if (!page.sections || page.sections.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        <p className="text-xl">No content added yet for this page.</p>
      </div>
    );
  }
  return (
    <main>
      {[...page.sections]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(section => (
          <SectionRenderer key={section.id} section={section} />
        ))}
    </main>
  );
};

export default PageRenderer;
